import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Activities, } from 'src/types/dealInsights';
import styles from './styles.module.scss';
import cx from 'classnames';
import Text from 'src/components/text';
import DOMPurify from 'dompurify';
import Tooltip from "src/components/tooltip";
import { activityIconMap } from 'src/utils/dealInsights.helper';
import { NavLink, useNavigate } from 'react-router-dom';

const DefaultUserAvatar = '/images/default_user_profile_background.png';

interface EmailProps {
  body: string,
}

function ShadowHtml({ html }: { html: string }) { // shadow html component to avoid XSS attacks
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hostRef.current) return;
    // create a shadow root and set the html to the shadow root
    const shadowRoot = hostRef.current.shadowRoot || hostRef.current.attachShadow({ mode: "open" }); 
    shadowRoot.innerHTML = html;
  }, [html]);

  return <div ref={hostRef} className={styles.shadowHtml} />;
}

const formatDuration = (ms: number | null) => {
  if (!ms) return '';

  const d = moment.duration(ms);

  const minutes = Math.floor(d.asMinutes());
  const seconds = d.seconds();

  if (minutes > 0 && seconds > 0) {
    return `${minutes}mins ${seconds}s`;
  }

  if (minutes > 0) {
    return `${minutes}mins`;
  }

  return `${seconds}s`;
};

const EmailCard = ({ body }: EmailProps) => {
  if (!body) return <></>;
  return (
    <div className={styles.detailedContainer}>
      {/* <div className={styles.pillsRow}> */}
      {/* // <div className={styles.pill}>
          //   <span className={styles.pillLabel}>From:</span> {details.from}
          // </div> */}

      {/* //<div className={styles.pill}>
            <span className={styles.pillLabel}>To:</span> {details.to}
          </div> */}
      {/* </div> */}
      <ShadowHtml html={body || ''} />
    </div>
  );
};

interface CallProps {
  body: string,
  duration?: number | null,
}

const CallCard = ({ body, duration }: CallProps) => {
  if (!body) return <></>;
  return (
    <div className={styles.detailedContainer}>
      {duration && <div className={styles.pillsRow}>
        <div className={styles.pill}>
          <i className="paperflite-clock" /> <span className={styles.pillLabel}>Duration:</span> {formatDuration(duration)}
        </div>
        {/* <div className={styles.pill}>
            <i className="paperflite-user-settings" /> <span className={styles.pillLabel}>prospect:</span> {details.prospects.join(', ')}
          </div> */}
      </div>}
      <ShadowHtml html={body || ''} />
    </div>
  );
};

interface DefaultDescriptionProps {
  text?: string;
}

const DefaultDescription = ({ text, }: DefaultDescriptionProps) => {
  return (
    <div className={styles.defaultDescContainer}>
      <ShadowHtml html={text || ''} />
    </div>
  );
};

const InfoCard = ({ label, value, icon, clickable, dealId }: {
  label: string;
  value: string;
  icon?: boolean;
  clickable?: boolean;
  dealId?: string;
}) => {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if(!clickable){
      e.preventDefault();
    }
  }
  return (
    <div className={styles.infoCard}>
      <p className={styles.label}>{label}:</p>
      <div className={styles.content}>
        {icon && <img src={DefaultUserAvatar} className={styles.avatar} />}
        <NavLink  
           className={cx(styles.value, { [styles.clickable]: clickable })}
           id={dealId}  
           onClick={handleClick}
           to={`/deal-insights/${dealId ?? '-'}`}>
          <Text text={value} tooltip={{ placement: "auto" }} />
        </NavLink>
      </div>
    </div>
  )
}

interface ActivityRowProps {
  activity: Activities;
  isDetailedView: boolean;
}

export const ActivityRow = ({ activity, isDetailedView }: ActivityRowProps) => {
  const iconClass = activityIconMap[activity.activityType] || 'paperflite-tick-unselected'; // icon mapped.

  const renderDetailedActivityContent = () => {
    switch (activity.activityType) {
      case 'EMAIL':
        return (
          <EmailCard body={activity.body} />
        );
      case 'CALL':
      case 'MEETING':
        return (
          <CallCard duration={activity.durationMs} body={activity.body} />
        );
      default:
        return <DefaultDescription text={activity.body} />
    }
  };


  const renderNormalActivityContent = () => {
    switch (activity.activityType) {
      case 'EMAIL':
        return (
          <div className={styles.infoWrapper}>
            <InfoCard label={'Sales Rep'} value={activity.ownerName || '-'} icon />
            <InfoCard label={'Deal Name'}  value={`${activity?.dealDetails?.name ?? '-'}`} clickable={!!activity?.dealDetails?.id} dealId={activity?.dealDetails?.id} />
          </div>
        );
      case 'CALL':
      case 'MEETING':
        return (
          <div className={styles.infoWrapper}>
            <InfoCard label={'Sales Rep'} value={activity.ownerName || '-'} icon />
            <InfoCard label={'Durations'} value={activity.durationMs ? formatDuration(activity.durationMs) : '-'} />
            <InfoCard label={'Deal Name'}  value={`${activity?.dealDetails?.name ?? '-'}`} clickable={!!activity?.dealDetails?.id} dealId={activity?.dealDetails?.id} />
          </div>
        );
      default:
        return (
          <div className={styles.infoWrapper}>
            <InfoCard label={'Sales Rep'} value={activity.ownerName || '-'} icon />
            <InfoCard label={'Deal Value'} value={activity.dealValue ? `$${activity.dealValue}` : '-'} />
            <InfoCard label={'Deal Name'}  value={`${activity?.dealDetails?.name ?? '-'}`} clickable={!!activity?.dealDetails?.id} dealId={activity?.dealDetails?.id} />
          </div>
        )
    }
  }

  const extractText = (html: string): string => {
    if (!html) return '';
    const clean = DOMPurify.sanitize(html);
    const temp = document.createElement('div');
    temp.innerHTML = clean;
    return temp.innerText.trim();
  };

  return (
    <div className={styles.activityRow}>
      <div className={styles.timelineGutter}>
        <div className={cx(styles.icon)}>
          <i className={iconClass} />
        </div>

        <div className={styles.verticalLine} />
      </div>

      {isDetailedView ?
        <div className={styles.deatiledViewArea}>
          <div className={styles.header}>
            {/* <div className={styles.title}>
              <Text text={extractText(activity.subject)} tooltip={{ placement: "auto" }} />
            </div> */}
            <p className={styles.title}>{extractText(activity.title)},</p>
            <p className={styles.timestamp}>{activity.occurredAt ? moment(activity.occurredAt).format("DD MMM'YY, hh:mm A") : ''}</p>
          </div>
          <p className={styles.subject}>{extractText(activity.subject)}</p>
          {renderDetailedActivityContent()}
        </div> :
        <div className={styles.normalViewArea}>
          <div className={styles.header}>
            <div className={styles.title}>
              <Text text={extractText(activity.title)} tooltip={{ placement: "auto" }} />
            </div>
            <p className={styles.timestamp}>{activity.occurredAt ? moment(activity.occurredAt).format("hh:mm A") : ''}</p>
          </div>
          <div className={styles.details}>
            <div dangerouslySetInnerHTML={{ __html: activity.subject || '' }} className={styles.subject} />
            { activity.body && <ShadowHtml html={activity.body || ''} />}
            {renderNormalActivityContent()}
          </div>
        </div>}
    </div>
  );
};

// Placeholder
export const DetailedViewPlaceHolder = () => {
  return (
    <div className={styles.activityRow}>
      <div className={styles.timelineGutter}>
        <div className={cx(styles.icon, styles.skeletonIcon)} />
        <div className={styles.verticalLine} />
      </div>

      <div className={styles.deatiledViewArea}>
        <div className={styles.header}>
          <div className={cx(styles.skeletonText, styles.skeletonTitle)} />
          <div className={cx(styles.skeletonText, styles.skeletonTime)} />
        </div>
        <div className={cx(styles.detailedContainer, styles.skeletonBox)}>
          <div className={cx(styles.skeletonText, styles.skeletonLine)} />
          <div className={cx(styles.skeletonText, styles.skeletonLine, styles.w75)} />
          <div className={cx(styles.skeletonText, styles.skeletonLine, styles.w50)} />
        </div>
      </div>
    </div>
  );
};

export const NormalViewPlaceHolder = () => {
  return (
    <div className={cx(styles.activityRow)}>
      <div className={styles.timelineGutter}>
        <div className={cx(styles.icon, styles.skeletonIcon)} />
        <div className={styles.verticalLine} />
      </div>

      <div className={styles.normalViewArea}>
        <div className={styles.header}>
          <div className={cx(styles.skeletonText, styles.skeletonTitle)} />
          <div className={cx(styles.skeletonText, styles.skeletonTime)} />
        </div>
        <div className={cx(styles.details, styles.skeletonBox)}>
          <div className={cx(styles.skeletonText, styles.skeletonLine)} />
          <div className={cx(styles.skeletonText, styles.skeletonLine, styles.w75)} />
          <div className={cx(styles.skeletonText, styles.skeletonLine, styles.w50)} />
        </div>
      </div>
    </div>
  );
};
